import { CoreQuery, Query } from "@gen/app-query";
import Box from "@mui/material/Box";
import { useEffect, useMemo, useState } from "react";

import { PublicSvgIcon } from "~/components/public_svg_icon";

export interface ClientLogoProps {
  clientId: string;
  alt: string;
  size: number;
  isAdmin?: boolean;
  showBackground?: boolean;
}

export function ClientLogo({
  clientId,
  alt,
  size,
  isAdmin = true,
  showBackground = false,
}: ClientLogoProps) {
  const [hasError, setHasError] = useState(false);
  const { data: adminCustomization, isFetching: isAdminFetching } =
    CoreQuery.useAdmin_getClientCustomizationQuery(clientId, {
      enabled: Boolean(clientId) && isAdmin,
    });
  const { data: coreCustomization, isFetching: isCoreFetching } =
    Query.useCore_getClientCustomizationQuery(clientId, {
      enabled: Boolean(clientId) && !isAdmin,
    });

  const customization = isAdmin ? adminCustomization : coreCustomization;
  const isFetching = isAdmin ? isAdminFetching : isCoreFetching;

  const logoUrl = useMemo(() => {
    if (customization?.logo_url) {
      return customization.logo_url;
    }
    if (customization?.favicon_url) {
      return customization.favicon_url;
    }
    return undefined;
  }, [customization?.logo_url, customization?.favicon_url]);

  useEffect(() => {
    setHasError(false);
  }, [logoUrl]);

  if (isFetching || !logoUrl || hasError) {
    return <PublicSvgIcon name="client_storefront" />;
  }

  const img = (
    <Box
      component="img"
      src={logoUrl}
      alt={alt}
      onError={() => setHasError(true)}
      sx={{
        width: size,
        height: size,
        objectFit: "contain",
      }}
    />
  );

  if (showBackground) {
    return (
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1,
          backgroundColor: "common.white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {img}
      </Box>
    );
  }

  return img;
}
